import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';

const PaymentStatusModal = ({ paymentParams, onClose }) => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [retryAttempted, setRetryAttempted] = useState(false);

  const verifyPayment = useCallback(async () => {
    try {
      // Base URL for your Azure Function
      const baseUrl = 'https://initiatepayment.azurewebsites.net/api/verify_transaction';
      const code = '4qEvYy0IrI3sOoSFaVeeagh1FghERbDnCf7DgqjbEHIBAzFuBqQkFA==';
      
      // Create a new URLSearchParams object and append the function code
      const queryParams = new URLSearchParams({ code });
      
      // Add the razorpay_payment_link_id parameter, which is what your backend expects
      if (paymentParams.razorpay_payment_link_id) {
        queryParams.append('razorpay_payment_link_id', paymentParams.razorpay_payment_link_id);
      }
      
      // Make a GET request
      const response = await fetch(`${baseUrl}?${queryParams}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setStatus('success');
      } else if (data.status === 'failed') {
        setStatus('failed');
        setError(data.message);
      } else {
        setStatus('unknown');
      }
    } catch (err) {
      setStatus('failed');
      setError('Failed to verify payment');
    }
  }, [paymentParams]);

  // Initial verification
  useEffect(() => {
    if (paymentParams?.razorpay_payment_link_id) {
      verifyPayment();
    }
  }, [paymentParams, verifyPayment]);

  // Handle unknown status with a single retry after 5 seconds
  useEffect(() => {
    let timeoutId;

    if (status === 'unknown' && !retryAttempted) {
      timeoutId = setTimeout(() => {
        setRetryAttempted(true);
        verifyPayment();
      }, 5000); // 5 seconds delay before retry
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [status, retryAttempted, verifyPayment]);

  const statusConfig = {
    processing: {
      icon: <Loader2 size={48} className="text-white animate-spin" />,
      title: 'Processing Payment',
      message: 'Please wait while we verify your payment...',
      color: 'text-white'
    },
    success: {
      icon: <CheckCircle size={48} className="text-green-500" />,
      title: 'Payment Successful',
      message: 'Your payment has been processed successfully!',
      color: 'text-green-500'
    },
    failed: {
      icon: <XCircle size={48} className="text-red-500" />,
      title: 'Payment Failed',
      message: error || 'Something went wrong with your payment.',
      color: 'text-red-500'
    },
    unknown: {
      icon: <AlertCircle size={48} className={`text-yellow-500 ${retryAttempted ? 'animate-pulse' : ''}`} />,
      title: 'Payment Status Unknown',
      message: retryAttempted 
        ? 'Payment status not recorded. Contact us at 8074428601 if you think this is a mistake.' 
        : 'Verifying payment status...',
      color: 'text-yellow-500'
    }
  };

  const currentStatus = statusConfig[status];

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm z-[100000] p-4">
      <div className="relative bg-black text-white shadow-xl rounded-xl p-8 
                    w-full max-w-md border border-gray-800 flex flex-col items-center">
        {/* Status Icon */}
        <div className="mb-6">
          {currentStatus.icon}
        </div>

        {/* Title */}
        <h2 className={`text-2xl font-bold mb-3 ${currentStatus.color}`}>
          {currentStatus.title}
        </h2>

        {/* Message */}
        <p className="text-gray-300 text-center mb-8">
          {currentStatus.message}
        </p>

        {/* Close button or action button based on status */}
        <button
          onClick={onClose}
          className="px-8 py-3 bg-white text-black font-bold rounded-lg
                   shadow-lg transform transition-all duration-300
                   hover:scale-105 hover:shadow-white/20 w-full max-w-xs"
        >
          {status === 'success' ? 'Continue' : 'Close'}
        </button>
      </div>
    </div>
  );
};

export { PaymentStatusModal };